import Scene from './Scene';
import { GameActions } from '../State/Game/GameActions';
import Game from '../Game';
import { AsyncActionStatus } from '../State/AsyncAction';
import { ResourceDriver } from '../Utility/Resource/ResourceManager';
import RenderSystem from '../System/Render/RenderSystem';
import { farmTransitionSceneId, GameScene, transitionSceneId } from '../State/Game/GameScene';
import { IGameState } from '../State/Game/IGameState';
import { unexpectedValue } from '../Utility/Types';
import { report } from '../report';
import { setIsTransitioning } from 'Utility/isTransitioning';

export enum SceneTransition {
    START = 'start',
    STARTED = 'started',
    END = 'end',
    ENDED = 'ended',
}

export default class SceneManager {
    private scenes: Map<string, Scene>;
    private activeScene: Scene;
    private loadingScene: boolean = false;

    constructor() {
        this.scenes = new Map<string, Scene>();
    }

    public addScene(sceneID: string, scene: Scene) {
        this.scenes.set(sceneID, scene);
    }

    public getScene(sceneID: string): Scene {
        return this.scenes.get(sceneID);
    }

    public async startScene(sceneID: string, sceneParams?: unknown, additive?: boolean) {
        const scene = this.scenes.get(sceneID);
        if (!scene) throw new Error(`Scene '${sceneID}' not found`);

        scene.params = sceneParams as any;

        if (!additive) {
            if (this.loadingScene) {
                // throw new Error(
                //     'Cannot load a scene while another scene is loading.'
                // );
                console.warn('Cannot load a scene while another scene is loading.');
                return;
            }

            // Toggle loading flag to prevent other scenes from being loaded while the
            // previous scene is unloaded and the new scene is loaded.
            this.loadingScene = true;

            const prevScene = this.activeScene;
            this.activeScene = scene;

            if (prevScene) {
                prevScene.unload();
                prevScene.loaded = false;
            }
        }

        scene.init();

        Game.resource.stop(ResourceDriver.TERTIARY);

        await scene.preload();
        scene.loaded = true;

        if (!additive) {
            this.loadingScene = false;

            // After this point a new scene may be loaded.
            GameActions.setSceneLoaded();
        }

        scene.start();
    }

    public getActiveScene(): Scene {
        return this.activeScene;
    }

    public handleStateChanged(state: IGameState) {
        if (state.activeScene.status === AsyncActionStatus.QUEUED) {
            console.info('[state] %o', state);
            switch (state.activeScene.result) {
                case GameScene.WORLD:
                    this.transitToScene(GameScene.WORLD, {
                        worldNumber: state.worldState.worldNumber,
                    });
                    break;
                case GameScene.LEVEL:
                    this.transitToScene(GameScene.LEVEL, {
                        levelNumber: state.levelState.levelNumber,
                        worldNumber: state.worldState.worldNumber,
                        attempts: state.levelState.attempts,
                    });
                    break;
                case GameScene.LUCKY_TURNIPS:
                    this.transitToScene(GameScene.LUCKY_TURNIPS);
                    break;
                case GameScene.MY_PET_MINI_GAME:
                    this.transitToScene(GameScene.MY_PET_MINI_GAME, state.activeScene.params);
                    break;
                case GameScene.FARM:
                    this.transitToScene(GameScene.FARM, state.activeScene.params);
                    break;
                case GameScene.PLINKO:
                    this.transitToScene(GameScene.PLINKO, state.activeScene.params);
                    break;
                default:
                    unexpectedValue(state.activeScene.result);
            }
            GameActions.setSceneLoading();
        }

        this.scenes.forEach(scene => {
            if (scene.loaded && scene.active) {
                scene.handleStateChange(state);
            }
        });
    }
    private async transitToScene(sceneID: string, sceneParams: unknown = {}) {
        const params = sceneParams as any;
        setIsTransitioning(true);
        Game.scene.getActiveScene().stop();
        const loadingSceneId = params.loadingSceneId ?? transitionSceneId;
        const transitionScene = this.scenes.get(loadingSceneId);
        transitionScene.once(SceneTransition.STARTED, async () => {
            try {
                await this.startScene(sceneID, sceneParams);
                transitionScene.emit(SceneTransition.END);
            } catch (error) {
                report('Transition failed', {
                    error,
                    once: true,
                    sampleRate: isOutOfMemoryError(error) ? 0.1 : 1,
                    tags: { sceneID },
                });
            }
        });

        if (!transitionScene.active) {
            this.scenes.entries();
            this.startScene(loadingSceneId, {}, true).then(() => {
                transitionScene.emit(SceneTransition.START);
            });
        } else {
            transitionScene.emit(SceneTransition.START);
        }
    }
    public update() {
        RenderSystem.renderUpdate();
    }
}

function isOutOfMemoryError(error: any) {
    return /\b(?:out of memory)\b/i.test(String(error?.message));
}
